import listboxNav from './listboxNav';

/**
 * Alpine data for Menu:Combobox — a filterable combobox with
 * server-rendered options (<twig:Menu:Option> blocks).
 *
 * Composes listboxNav for keyboard navigation. Options are
 * rendered by Twig and discovered from the DOM at init time.
 * Filtering works by toggling the `hidden` attribute on
 * non-matching options and empty groups.
 *
 * Config: { value, label, noResultsText, disabled }
 * Dispatches: menu-combobox-change { value, label, thumbnail, description }
 *
 * Template: templates/components/Menu/Combobox.html.twig
 */
export default function menuCombobox(config = {}) {
  const nav = listboxNav();

  return {
    ...nav,
    search: '',
    value: config.value || '',
    label: config.label || '',
    thumbnail: '',
    description: '',
    noResultsText: config.noResultsText || 'No results found',
    disabled: config.disabled || false,

    _allItems: [],
    _groups: [],
    _visibleCount: 0,

    init() {
      this.$nextTick(() => {
        this._buildIndex();
        this._resolveSelectedMeta();

        if (this.$refs.input) {
          this.$refs.input.value = this.label;
        }
      });

      this.initHighlightScroll();
      this.initPageShowResync();
    },

    _onPageShowResync() {
      if (this.$refs.input) {
        this.$refs.input.value = this.label;
      }
    },

    _buildIndex() {
      const listbox = this.$refs.listbox;
      if (!listbox) return;

      this._allItems = [];
      listbox.querySelectorAll('[role="option"]').forEach((el) => {
        const description =
          el.querySelector('[data-option-description]')?.textContent || '';
        this._allItems.push({
          el,
          value: (el.dataset.value || '').toLowerCase(),
          text: el.textContent.trim().toLowerCase(),
          description: description.trim().toLowerCase(),
        });
      });

      this._groups = [];
      listbox.querySelectorAll('[role="group"]').forEach((groupEl) => {
        const wrapper = groupEl.closest('[role="presentation"]');
        if (wrapper) {
          const optionEls = Array.from(
            groupEl.querySelectorAll('[role="option"]'),
          );
          const label =
            groupEl.getAttribute('aria-label')?.toLowerCase() || '';
          this._groups.push({ el: wrapper, optionEls, label });
        }
      });

      const groupMap = new Map(
        this._groups.map((g) => [g.el, g.label]),
      );
      for (const item of this._allItems) {
        const wrapper = item.el.closest('[role="presentation"]');
        item.groupLabel = wrapper ? groupMap.get(wrapper) || '' : '';
      }

      this._reindex();
    },

    _reindex() {
      const listbox = this.$refs.listbox;
      if (!listbox) return;

      const prefix =
        listbox.id?.replace(/-listbox$/, '') || 'menu-combobox';
      let idx = 0;
      for (const item of this._allItems) {
        if (!item.el.hidden) {
          item.el.id = `${prefix}-option-${idx}`;
          item.el.dataset.itemIndex = idx;
          idx++;
        } else {
          delete item.el.dataset.itemIndex;
          item.el.removeAttribute('id');
        }
      }
      this._visibleCount = idx;
    },

    _resolveSelectedMeta() {
      if (!this.value || !this.$refs.listbox) {
        this.thumbnail = '';
        this.description = '';
        return;
      }
      const el = this.$refs.listbox.querySelector(
        `[data-value="${CSS.escape(this.value)}"]`,
      );
      this.thumbnail = el?.querySelector('img')?.src || '';
      this.description =
        el
          ?.querySelector('[data-option-description]')
          ?.textContent.trim() || '';
    },

    _applyFilter() {
      const term = this.search.trim().toLowerCase();

      for (const item of this._allItems) {
        if (!term) {
          item.el.hidden = false;
        } else {
          const match =
            item.value.includes(term) ||
            item.text.includes(term) ||
            item.description.includes(term) ||
            item.groupLabel.includes(term);
          item.el.hidden = !match;
        }
      }

      for (const group of this._groups) {
        const hasVisible = group.optionEls.some((el) => !el.hidden);
        group.el.hidden = !hasVisible;
      }

      this._reindex();

      if (this.$refs.noResults) {
        this.$refs.noResults.hidden = this._visibleCount > 0;
      }
    },

    getItemCount() {
      return this._visibleCount;
    },

    isItemDisabled(index) {
      const listbox = this.$refs.listbox;
      if (!listbox) return false;
      const el = listbox.querySelector(
        `[data-item-index="${index}"]`,
      );
      return el?.hasAttribute('data-disabled') ?? false;
    },

    openDropdown() {
      if (this.disabled || this.open) return;

      this.open = true;
      this.search = '';
      this._applyFilter();
      this.highlightedIndex = this.nextEnabledIndex(0, 1);

      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.value = '';
        }
      });
    },

    close() {
      this.open = false;
      this.search = '';
      this.highlightedIndex = -1;
      this._applyFilter();

      this.$nextTick(() => {
        if (this.$refs.input) {
          this.$refs.input.value = this.label;
        }
      });
    },

    selectHighlighted() {
      if (this.highlightedIndex < 0) return;
      const listbox = this.$refs.listbox;
      if (!listbox) return;
      const el = listbox.querySelector(
        `[data-item-index="${this.highlightedIndex}"]`,
      );
      if (!el) return;
      el.querySelector('button')?.click();
    },

    select(value, label) {
      this.value = value;
      this.label = label;
      this._resolveSelectedMeta();
      this.close();

      this.$dispatch('menu-combobox-change', {
        value,
        label,
        thumbnail: this.thumbnail,
        description: this.description,
      });
    },

    clear() {
      this.value = '';
      this.label = '';
      this.thumbnail = '';
      this.description = '';

      if (this.$refs.input) {
        this.$refs.input.value = '';
      }

      this.$dispatch('menu-combobox-change', {
        value: '',
        label: '',
      });
    },

    handleInput(event) {
      if (this.disabled) return;

      if (!this.open) {
        this.open = true;
        this.search = '';
        this._applyFilter();
        this.highlightedIndex = this.nextEnabledIndex(0, 1);

        if (this.$refs.input) {
          this.$refs.input.value = event.data || '';
        }
      }

      this.search = event.target.value;
      this._applyFilter();

      this.highlightedIndex =
        this._visibleCount > 0
          ? this.nextEnabledIndex(0, 1)
          : -1;
    },

    handleKeydown(event) {
      if (this.disabled) return;

      const { key, altKey } = event;

      // Alt+ArrowDown opens list without highlighting (user may type first)
      if (altKey && key === 'ArrowDown') {
        event.preventDefault();
        if (!this.open) {
          this.open = true;
          this.search = '';
          this._applyFilter();
          this.highlightedIndex = -1;
          this.$nextTick(() => {
            if (this.$refs.input) {
              this.$refs.input.value = '';
            }
          });
        }
        return;
      }

      // Home/End navigate list only when cursor is at input boundary
      if (
        key === 'Home' &&
        this.open &&
        this.$refs.input.selectionStart !== 0
      ) {
        return;
      }
      if (
        key === 'End' &&
        this.open &&
        this.$refs.input.selectionStart !== this.search.length
      ) {
        return;
      }

      // Space is for typing, not list navigation
      if (key === ' ') return;

      // Enter does not open when closed
      if (key === 'Enter' && !this.open) return;

      // Escape clears value when closed
      if (key === 'Escape' && !this.open && this.value) {
        event.preventDefault();
        this.clear();
        return;
      }

      nav.handleKeydown.call(this, event);
    },
  };
}
